import { useMemo } from 'react';

import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import {
  BusinessWebsiteMatchStatus,
  ReportBusinessWebsite,
} from 'dashboard/models/report/business-website';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';

interface BusinessWebsiteProps {
  report: ReportBusinessWebsite;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const MatchSticker = ({ hasMatch }: { hasMatch: boolean | undefined }) => {
  return hasMatch ? (
    <Sticker variant='success'>Matched</Sticker>
  ) : (
    <Sticker variant='danger'>No match</Sticker>
  );
};

const BusinessWebsite = ({ report, isRedacted, multiCardFormat = false }: BusinessWebsiteProps) => {
  const {
    query: { website: websiteInput },
    'match-status-website': matchStatusWebsite,
    'business-name': matchedBusinessName,
    'has-match': hasMatch,
    result: { website: matchedWebsite, 'associated-websites': associatedWebsites },
  } = report.attributes;

  const matchStatusWebsiteLabel: string = useMemo(() => {
    switch (matchStatusWebsite) {
      case BusinessWebsiteMatchStatus.Full:
        return 'Full Match';
      case BusinessWebsiteMatchStatus.Partial:
        return 'Partial Match';
      case BusinessWebsiteMatchStatus.None:
      default:
        return 'No Match';
    }
  }, [matchStatusWebsite]);

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      <>
        {multiCardFormat && <Card.Title>Website Report</Card.Title>}
        <AttributeGrid colCount={3}>
          <Attribute
            label='Report Status'
            placeholder='N/A'
            value={<MatchSticker hasMatch={hasMatch} />}
            redacted={isRedacted}
          />
          <Attribute
            label='Business Found'
            placeholder='N/A'
            value={hasMatch ? matchedBusinessName : null}
            redacted={isRedacted}
          />
          <Attribute
            label='Associated Websites'
            placeholder='N/A'
            value={
              hasMatch
                ? associatedWebsites.map((associatedWebsite) => (
                    <div key={associatedWebsite.url}>
                      <a
                        href={ensureScheme(associatedWebsite.url)}
                        target='_blank'
                        rel='noreferrer'
                      >
                        {associatedWebsite.url}
                      </a>
                    </div>
                  ))
                : null
            }
            redacted={isRedacted}
          />
        </AttributeGrid>
        {hasMatch && (
          <>
            <Spacer height={2} />
            <Card.SectionHeader>Website Comparison</Card.SectionHeader>
            <AttributeGrid colCount={3}>
              <Attribute
                label='Comparison Result'
                value={<Sticker variant='grey'>{matchStatusWebsiteLabel}</Sticker>}
                redacted={isRedacted}
              />
              <Attribute label='Website Input' value={websiteInput} redacted={isRedacted} />
              <Attribute
                label='Website Matched'
                placeholder='N/A'
                value={
                  matchedWebsite ? (
                    <a href={ensureScheme(matchedWebsite.url)} target='_blank' rel='noreferrer'>
                      {matchedWebsite.url}
                    </a>
                  ) : null
                }
                redacted={isRedacted}
              />
            </AttributeGrid>
          </>
        )}
      </>
    </ReportResultLayout>
  );
};

const ensureScheme = (url: string) => {
  return url.startsWith('http') ? url : `https://${url}`;
};

export default BusinessWebsite;
